import Glide from '@glidejs/glide';
import { settings as raySettings } from '@wework/ray-core';
import { trackSliderInteraction } from './analytics';

const TOUCH_EVENT_INTERCEPTOR_SELECTOR = '.we-glide-touch-event-interceptor';
const PREVIOUS_ARROW_SELECTOR = '.slider__arrow-btn--left';
const NEXT_ARROW_SELECTOR = '.slider__arrow-btn--right';
const NAVIGATION_ARROWS_SELECTOR = '.slider__arrow-btn';

const DEFAULT_GLIDE_SETTINGS = {
  touchAngle: 45,
  angleDetectionSwipeThreshold: 25,
  sliderActiveClass: 'we-glide-slider--active'
};

export const RESPONSIVE_GLIDE_SETTINGS = {
  gap: 24,
  breakpoints: {
    [raySettings.breakpointsInPixels.tablet]: {
      gap: 8
    },
    [raySettings.breakpointsInPixels.desktop]: {
      gap: 24
    }
  }
};

export default class WeGlide {
  constructor(selector, overrides) {
    this.settings = {
      ...DEFAULT_GLIDE_SETTINGS,
      ...RESPONSIVE_GLIDE_SETTINGS,
      ...overrides
    };
    this.isScrolling = undefined;
    this.touches = {};
    this.element = document.querySelector(selector);
    this.glide = new Glide(selector, this.settings);
    this.touchEventInterceptor = this.element.querySelector(TOUCH_EVENT_INTERCEPTOR_SELECTOR);
    this.navigationArrows = this.element.querySelectorAll(NAVIGATION_ARROWS_SELECTOR);
    this.initControlArrowsElements();
    this.addSliderActiveClass();
    this.addTransitionActiveClass();
    this.hidePreviousArrowOnFirstSlide();
    this.trackArrowsClickInteractions();
    this.trackSwipeInteractions();
    if (this.touchEventInterceptor) {
      this.touchStartHandler = this.touchStart.bind(this);
      this.touchEndHandler = this.touchEnd.bind(this);
      this.touchMoveHandler = this.touchMoveHandler.bind(this);
      this.addTouchEventInterceptorHandlers();
    }
  }

  removeNavigationArrows() {
    this.navigationArrows.forEach(arrowEl => arrowEl.classList.add('hidden'));
  }

  addTransitionActiveClass() {
    if (this.settings.transitionActiveClass) {
      this.glide.on('run', () => {
        this.element.classList.add(this.settings.transitionActiveClass);
      });
      this.glide.on('move.after', () => {
        this.element.classList.remove(this.settings.transitionActiveClass);
      });
    }
  }

  addSliderActiveClass() {
    this.glide.on('mount.after', () => {
      this.element.classList.add(this.settings.sliderActiveClass);
    });
  }

  initControlArrowsElements() {
    if (this.settings.direction === 'rtl') {
      this.previousArrow = this.element.querySelector(NEXT_ARROW_SELECTOR);
      this.nextArrow = this.element.querySelector(PREVIOUS_ARROW_SELECTOR);
    } else {
      this.previousArrow = this.element.querySelector(PREVIOUS_ARROW_SELECTOR);
      this.nextArrow = this.element.querySelector(NEXT_ARROW_SELECTOR);
    }
  }

  addTouchEventInterceptorHandlers() {
    this.processTouchEventInterceptorHandlers('addEventListener');
  }

  removeTouchEventInterceptorHandlers() {
    this.processTouchEventInterceptorHandlers('removeEventListener');
  }

  processTouchEventInterceptorHandlers(action) {
    if (this.touchEventInterceptor) {
      this.touchEventInterceptor[action]('touchstart', this.touchStartHandler, { capture: true });
      this.touchEventInterceptor[action]('touchend', this.touchEndHandler, { passive: false, capture: true });
      this.touchEventInterceptor[action]('touchmove', this.touchMoveHandler, { passive: false, capture: true });
    }
  }

  touchEnd(e) {
    if (this.isScrolling) {
      e.stopPropagation();
    }
  }

  touchStart(e) {
    this.isScrolling = undefined;
    this.touches.currentX = e.type === 'touchstart' ? e.targetTouches[0].pageX : e.pageX;
    this.touches.currentY = e.type === 'touchstart' ? e.targetTouches[0].pageY : e.pageY;
    this.touches.startX = this.touches.currentX;
    this.touches.startY = this.touches.currentY;
  }

  touchMoveHandler(e) {
    if (e.type === 'mousemove') {
      return;
    }

    if (this.isScrolling) {
      e.stopPropagation();
      return;
    }

    const isTouchMoveEvent = e.type === 'touchmove';
    const targetTouch = isTouchMoveEvent && e.targetTouches && (e.targetTouches[0] || e.changedTouches[0]);
    const pageX = isTouchMoveEvent ? targetTouch.pageX : e.pageX;
    const pageY = isTouchMoveEvent ? targetTouch.pageY : e.pageY;

    if (e.targetTouches && e.targetTouches.length > 1) {
      return;
    }

    this.touches.currentX = pageX;
    this.touches.currentY = pageY;
    this.checkIfPageIsBeingScrolled();

    // Prevent slider interaction if page is being scrolled
    if (this.isScrolling) {
      e.stopPropagation();
    }

    // Prevent page scrolling if slider is being swiped
    if (!this.isScrolling) {
      e.preventDefault();
    }
  }

  checkIfPageIsBeingScrolled() {
    const diffX = this.touches.currentX - this.touches.startX;
    const diffY = this.touches.currentY - this.touches.startY;
    const { touchAngle, angleDetectionSwipeThreshold } = this.settings;

    if (this.isScrolling === undefined) {
      let currentTouchAngle;
      if (this.touches.currentY === this.touches.startY) {
        this.isScrolling = false;
        return;
      }

      if (diffX * diffX + diffY * diffY >= angleDetectionSwipeThreshold) {
        currentTouchAngle = (Math.atan2(Math.abs(diffY), Math.abs(diffX)) * 180) / Math.PI;
        this.isScrolling = currentTouchAngle > touchAngle;
      }
    }
  }

  /**
   * TODO hide nextArrow if not needed, use: [this.glide.settings.perView, this.element.querySelectorAll('.glide__slide').length]
   * ticket: https://jira.weworkers.io/browse/MPWW-13336
   */
  hidePreviousArrowOnFirstSlide() {
    if (!this.previousArrow) return;

    this.glide.on('move', ({ movement }) => {
      const isFirstSlide = movement === 0;
      if (isFirstSlide) {
        this.previousArrow.classList.add('hidden');
      } else {
        this.previousArrow.classList.remove('hidden');
      }
    });
  }

  disableOnTablet() {
    this.glide.on('resize', () => {
      if (window.innerWidth < raySettings.breakpointsInPixels.tablet) {
        this.glide.enable();
      } else if (window.innerWidth > raySettings.breakpointsInPixels.tablet && this.glide) {
        this.glide.disable();
      }
    });
  }

  trackArrowsClickInteractions() {
    if (this.nextArrow && this.sliderName) {
      this.nextArrow.addEventListener('click', () => {
        trackSliderInteraction(this.sliderName, this.glide.index + 1, 'arrow');
      });
    }

    if (this.previousArrow && this.sliderName) {
      this.previousArrow.addEventListener('click', () => {
        trackSliderInteraction(this.sliderName, this.glide.index - 1, 'arrow');
      });
    }
  }

  trackSwipeInteractions() {
    this.glide.on('swipe.end', () => {
      trackSliderInteraction(this.sliderName, this.glide.index, 'swipe');
    });
  }

  mount() {
    this.glide.mount();
  }

  destroy() {
    this.removeTouchEventInterceptorHandlers();
    this.glide.destroy();
  }

  goToNext() {
    this.glide.go('>');
  }

  get sliderName() {
    return this.element.dataset.name;
  }
}
